import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Compass,
  CircleNotch,
  CaretRight,
  CaretLeft,
  CheckCircle,
  Warning,
  FloppyDisk,
  ArrowSquareOut,
  Sparkle,
  MapPin,
} from "@phosphor-icons/react";

// ---------------- Static intake questions (7 total) ----------------
// Kept small; Leo will read the aggregate to classify. Question text is
// carefully worded to preserve the KB's signal boundaries.
const INTAKE = [
  {
    id: "trigger",
    prompt: "What's prompting this review?",
    testid: "intake-trigger",
    type: "single",
    options: [
      { value: "spring_clean", label: "Ordinary tidying / spring clean" },
      { value: "future_move", label: "Preparing for an eventual smaller home" },
      { value: "scheduled_move", label: "Scheduled move within 12 months" },
      { value: "estate_event", label: "Estate settlement (executor, probate)" },
      { value: "divorce", label: "Divorce or separation" },
      { value: "bankruptcy", label: "Bankruptcy or debtor's process" },
      { value: "insurance_loss", label: "Insurance / disaster loss" },
      { value: "other", label: "Something else" },
    ],
  },
  {
    id: "timeline",
    prompt: "How much time do you have?",
    testid: "intake-timeline",
    type: "single",
    options: [
      { value: "this_weekend", label: "This weekend" },
      { value: "this_month", label: "Within this month" },
      { value: "under_12mo", label: "Within 12 months" },
      { value: "12_36mo", label: "12 to 36 months" },
      { value: "no_date", label: "No specific date" },
    ],
  },
  {
    id: "scope",
    prompt: "What's the scope?",
    testid: "intake-scope",
    type: "single",
    options: [
      { value: "single_asset", label: "One specific item" },
      { value: "one_room", label: "A room or closet" },
      { value: "multi_room", label: "Several rooms / categories" },
      { value: "whole_home", label: "Whole home" },
    ],
  },
  {
    id: "value",
    prompt: "Any items likely to exceed $5,000 fair market value?",
    testid: "intake-value",
    type: "single",
    options: [
      { value: "no", label: "No" },
      { value: "maybe", label: "Maybe one or two" },
      { value: "yes", label: "Yes, several" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "third_party",
    prompt: "Are any third parties involved in the decision?",
    testid: "intake-third-party",
    type: "single",
    options: [
      { value: "none", label: "No one else" },
      { value: "family", label: "Family / heirs, informally" },
      { value: "attorney", label: "An attorney or executor" },
      { value: "court_insurer", label: "A court, insurer, or regulator" },
    ],
  },
  {
    id: "hazards",
    prompt: "Are any of these in scope?",
    testid: "intake-hazards",
    type: "multi",
    options: [
      { value: "electronics", label: "Old electronics or drives with personal data" },
      { value: "hazardous", label: "Paint, chemicals, batteries, ammunition" },
      { value: "medications", label: "Expired medications" },
      { value: "digital_assets", label: "Digital accounts, crypto, or media libraries" },
      { value: "none", label: "None of these" },
    ],
  },
  {
    id: "family_input",
    prompt: "How much family input do you want?",
    testid: "intake-family-input",
    type: "single",
    options: [
      { value: "none", label: "None — my call" },
      { value: "inform", label: "Inform them after" },
      { value: "consult", label: "Consult before deciding" },
      { value: "decide_together", label: "Decide together" },
    ],
  },
];

// ---------------- US States + Canadian Provinces ----------------
const US_STATES = ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","District of Columbia","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"];
const CA_PROVINCES = ["Alberta","British Columbia","Manitoba","New Brunswick","Newfoundland and Labrador","Northwest Territories","Nova Scotia","Nunavut","Ontario","Prince Edward Island","Quebec","Saskatchewan","Yukon"];

export default function TransitionGuideDrawer({
  open,
  onOpenChange,
  assetId,
  assetSnapshot,
  onSaved,
}) {
  const { user, canWrite, updateProfile } = useAuth();
  const [step, setStep] = useState("jurisdiction"); // jurisdiction | intake | result | guide
  const [answers, setAnswers] = useState({});
  const [jurisdictionDraft, setJurisdictionDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [classification, setClassification] = useState(null);
  const [guideData, setGuideData] = useState(null);
  const [userNotes, setUserNotes] = useState("");

  // When drawer opens, decide starting step
  useEffect(() => {
    if (!open) return;
    setAnswers({});
    setUserNotes("");
    setClassification(null);
    setGuideData(null);
    if (!user?.jurisdiction) {
      setStep("jurisdiction");
      setJurisdictionDraft("");
    } else {
      setStep("intake");
    }
  }, [open, user?.jurisdiction]);

  const saveJurisdiction = useCallback(async () => {
    if (!jurisdictionDraft.trim()) {
      toast.error("Please pick your state or province.");
      return;
    }
    setBusy(true);
    const r = await updateProfile({ jurisdiction: jurisdictionDraft.trim() });
    setBusy(false);
    if (r.ok) {
      toast.success("Jurisdiction saved.");
      setStep("intake");
    } else {
      toast.error(r.error || "Could not save jurisdiction.");
    }
  }, [jurisdictionDraft, updateProfile]);

  const submitIntake = useCallback(async () => {
    // Require answers to at least trigger + timeline
    if (!answers.trigger || !answers.timeline) {
      toast.error("Answer the first two questions before continuing.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/transition/classify", {
        answers,
        asset_id: assetId,
        asset_snapshot: assetSnapshot,
        jurisdiction: user?.jurisdiction,
      });
      setClassification(r.data);
      setStep("result");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Classification failed.");
    } finally {
      setBusy(false);
    }
  }, [answers, assetId, assetSnapshot, user?.jurisdiction]);

  const generateGuide = useCallback(async () => {
    if (!classification?.scenario_key) return;
    setBusy(true);
    try {
      const r = await api.post("/transition/guide", {
        scenario_key: classification.scenario_key,
        jurisdiction: user?.jurisdiction,
        asset_id: assetId,
        asset_snapshot: assetSnapshot,
        intake_answers: answers,
      });
      setGuideData(r.data);
      setStep("guide");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Guide generation failed.");
    } finally {
      setBusy(false);
    }
  }, [classification?.scenario_key, user?.jurisdiction, assetId, assetSnapshot, answers]);

  const savePlan = useCallback(async () => {
    if (!guideData) return;
    setBusy(true);
    try {
      const r = await api.post("/transition/plans", {
        asset_id: assetId,
        asset_snapshot: assetSnapshot,
        scenario_key: classification.scenario_key,
        jurisdiction: user?.jurisdiction,
        intake_answers: answers,
        classification,
        guide: guideData.guide,
        market_practice: guideData.market_practice,
        user_notes: userNotes || null,
      });
      toast.success("Transition Plan saved to your ledger.");
      onSaved?.(r.data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed.");
    } finally {
      setBusy(false);
    }
  }, [guideData, assetId, assetSnapshot, classification, user?.jurisdiction, answers, userNotes, onSaved, onOpenChange]);

  const canSubmitIntake = answers.trigger && answers.timeline;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl flex flex-col p-0 bg-[#F9F8F6]"
        data-testid="transition-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-[#E0DDD6] bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#1A3622] flex items-center justify-center border border-[#0F2818] shrink-0">
              <Compass size={18} weight="regular" color="#F9F8F6" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="font-serif-display text-xl text-[#0A192F] leading-tight">
                Transition Guide
              </SheetTitle>
              <SheetDescription className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                {assetSnapshot?.name ? `Leo · ${assetSnapshot.name}` : "Leo · plan a transition"}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {step === "jurisdiction" && (
            <JurisdictionStep
              draft={jurisdictionDraft}
              setDraft={setJurisdictionDraft}
              onSave={saveJurisdiction}
              busy={busy}
            />
          )}
          {step === "intake" && (
            <IntakeStep
              answers={answers}
              setAnswers={setAnswers}
              onSubmit={submitIntake}
              canSubmit={canSubmitIntake}
              busy={busy}
              canWrite={canWrite}
              jurisdiction={user?.jurisdiction}
              onEditJurisdiction={() => setStep("jurisdiction")}
            />
          )}
          {step === "result" && classification && (
            <ResultStep
              classification={classification}
              onBack={() => setStep("intake")}
              onContinue={generateGuide}
              busy={busy}
              onOverride={(newKey) => setClassification({ ...classification, scenario_key: newKey })}
            />
          )}
          {step === "guide" && guideData && (
            <GuideStep
              data={guideData}
              userNotes={userNotes}
              setUserNotes={setUserNotes}
              onBack={() => setStep("result")}
              onSave={savePlan}
              busy={busy}
              canWrite={canWrite}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------- Sub-steps ----------------

function JurisdictionStep({ draft, setDraft, onSave, busy }) {
  const [country, setCountry] = useState("US");
  const options = country === "US" ? US_STATES : CA_PROVINCES;
  return (
    <div className="space-y-5" data-testid="transition-jurisdiction-step">
      <div className="bg-white border border-[#C5A059] border-l-4 px-5 py-4">
        <div className="flex items-start gap-3">
          <MapPin size={18} className="text-[#1A3622] shrink-0 mt-0.5" />
          <div>
            <div className="font-serif-display text-xl text-[#0A192F] leading-tight">
              Where are you based?
            </div>
            <p className="text-sm text-[#5C5F58] mt-1">
              U.S. state or Canadian province. Leo needs this to localise probate, tax, and disposal
              guidance — it&apos;s a hard requirement in the Knowledge Base.
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setCountry("US")}
          data-testid="jurisdiction-country-us"
          className={`flex-1 py-2 text-sm font-medium border ${country === "US" ? "bg-[#1A3622] text-[#F9F8F6] border-[#0F2818]" : "bg-white text-[#0A192F] border-[#E0DDD6]"}`}
        >
          United States
        </button>
        <button
          onClick={() => setCountry("CA")}
          data-testid="jurisdiction-country-ca"
          className={`flex-1 py-2 text-sm font-medium border ${country === "CA" ? "bg-[#1A3622] text-[#F9F8F6] border-[#0F2818]" : "bg-white text-[#0A192F] border-[#E0DDD6]"}`}
        >
          Canada
        </button>
      </div>

      <div>
        <label className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
          {country === "US" ? "State" : "Province / Territory"}
        </label>
        <select
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="jurisdiction-select"
          className="w-full border border-[#E0DDD6] bg-white px-3 py-2 text-sm focus:border-[#1A3622] focus:outline-none"
        >
          <option value="">— Select —</option>
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>

      <button
        onClick={onSave}
        disabled={busy || !draft}
        data-testid="jurisdiction-save-btn"
        className="w-full bg-[#1A3622] text-[#F9F8F6] py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        {busy ? <><CircleNotch size={14} className="animate-spin" /> Saving…</> : <>Save and continue <CaretRight size={14} weight="bold" /></>}
      </button>
    </div>
  );
}

function IntakeStep({ answers, setAnswers, onSubmit, canSubmit, busy, canWrite, jurisdiction, onEditJurisdiction }) {
  const set = (id, value, multi = false) => {
    setAnswers((prev) => {
      if (multi) {
        const existing = new Set(prev[id] || []);
        if (existing.has(value)) existing.delete(value);
        else existing.add(value);
        return { ...prev, [id]: Array.from(existing) };
      }
      return { ...prev, [id]: value };
    });
  };

  return (
    <div className="space-y-6" data-testid="transition-intake-step">
      <div className="flex items-center justify-between bg-white border border-[#E0DDD6] px-4 py-2">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
          Jurisdiction: <span className="text-[#0A192F] normal-case">{jurisdiction || "not set"}</span>
        </div>
        <button
          onClick={onEditJurisdiction}
          data-testid="edit-jurisdiction-btn"
          className="text-xs text-[#1A3622] underline underline-offset-2 hover:text-[#0F2818]"
        >
          Change
        </button>
      </div>

      <div>
        <div className="font-serif-display text-xl text-[#0A192F]">A short intake.</div>
        <p className="text-sm text-[#5C5F58] mt-1">
          Answer honestly. Leo uses your answers to route you into the right kind of guidance —
          not to judge how much you have or don&apos;t.
        </p>
      </div>

      {INTAKE.map((q, idx) => (
        <div key={q.id} className="bg-white border border-[#E0DDD6] px-4 py-4" data-testid={q.testid}>
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
            Question {idx + 1} of {INTAKE.length}
          </div>
          <div className="text-sm text-[#0A192F] font-medium mb-3">{q.prompt}</div>
          <div className={`grid ${q.type === "multi" ? "grid-cols-1 gap-1.5" : "grid-cols-1 sm:grid-cols-2 gap-1.5"}`}>
            {q.options.map((o) => {
              const selected = q.type === "multi"
                ? (answers[q.id] || []).includes(o.value)
                : answers[q.id] === o.value;
              return (
                <button
                  key={o.value}
                  onClick={() => set(q.id, o.value, q.type === "multi")}
                  data-testid={`${q.testid}-opt-${o.value}`}
                  className={`text-left px-3 py-2 text-xs border transition-colors ${selected ? "bg-[#1A3622] text-[#F9F8F6] border-[#0F2818]" : "bg-white text-[#0A192F] border-[#E0DDD6] hover:border-[#1A3622]"}`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <button
        onClick={onSubmit}
        disabled={busy || !canSubmit || !canWrite}
        data-testid="intake-submit-btn"
        className="w-full bg-[#1A3622] text-[#F9F8F6] py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        {busy ? <><CircleNotch size={14} className="animate-spin" /> Classifying with Leo…</> : <><Sparkle size={14} weight="bold" /> Classify my situation</>}
      </button>
    </div>
  );
}

function ResultStep({ classification, onBack, onContinue, busy, onOverride }) {
  const [showOverride, setShowOverride] = useState(false);
  const s = classification.scenario || {};
  const scenarios = ["routine_declutter", "smart_sizing", "downsizing", "formal_dispensation"];
  const labels = {
    routine_declutter: "Routine Decluttering",
    smart_sizing: "Smart-Sizing",
    downsizing: "Downsizing",
    formal_dispensation: "Formal Asset Dispensation",
  };
  const confBadge = {
    high: { color: "#1A3622", label: "High confidence" },
    medium: { color: "#B87333", label: "Medium confidence" },
    low: { color: "#6B1718", label: "Low confidence" },
  }[classification.confidence] || { color: "#5C5F58", label: "—" };

  return (
    <div className="space-y-5" data-testid="transition-result-step">
      <button onClick={onBack} data-testid="result-back-btn" className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] hover:text-[#1A3622] inline-flex items-center gap-1">
        <CaretLeft size={12} /> Back to intake
      </button>

      <div className="bg-white border border-[#E0DDD6] px-5 py-5">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
          Leo&apos;s classification
        </div>
        <h2 className="font-serif-display text-3xl text-[#0A192F] mt-2 leading-tight" data-testid="result-scenario-label">
          {labels[classification.scenario_key]}
        </h2>
        <p className="text-sm text-[#5C5F58] italic mt-2">{s.one_liner}</p>
        <div className="mt-3 inline-flex items-center gap-2 px-2 py-1 border" style={{ borderColor: confBadge.color, color: confBadge.color }}>
          <span className="font-mono-ledger text-[10px] uppercase tracking-[0.2em]">{confBadge.label}</span>
        </div>
        {classification.rationale && (
          <div className="mt-3 pt-3 border-t border-[#E0DDD6]">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">Rationale</div>
            <p className="text-sm text-[#0A192F]" data-testid="result-rationale">{classification.rationale}</p>
          </div>
        )}
      </div>

      {(classification.flags || []).length > 0 && (
        <div className="bg-[#F4E9D2] border border-[#C5A059] px-4 py-3 text-xs text-[#5C3D08]">
          <div className="flex items-start gap-2">
            <Warning size={14} className="mt-0.5 shrink-0" />
            <div>
              <strong>Leo flagged:</strong> {classification.flags.join(", ")}
            </div>
          </div>
        </div>
      )}

      <div>
        <button
          onClick={() => setShowOverride((v) => !v)}
          data-testid="show-override-btn"
          className="text-xs text-[#1A3622] underline underline-offset-2"
        >
          {showOverride ? "Hide" : "Not the right scenario? Pick another."}
        </button>
        {showOverride && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {scenarios.map((k) => (
              <button
                key={k}
                onClick={() => onOverride(k)}
                data-testid={`override-${k}`}
                className={`text-left px-3 py-2 text-xs border transition-colors ${classification.scenario_key === k ? "bg-[#1A3622] text-[#F9F8F6] border-[#0F2818]" : "bg-white text-[#0A192F] border-[#E0DDD6] hover:border-[#1A3622]"}`}
              >
                {labels[k]}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={onContinue}
        disabled={busy}
        data-testid="result-continue-btn"
        className="w-full bg-[#1A3622] text-[#F9F8F6] py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        {busy ? <><CircleNotch size={14} className="animate-spin" /> Composing 6-dimension guidance…</> : <><Sparkle size={14} weight="bold" /> Compose my guide</>}
      </button>
    </div>
  );
}

function GuideStep({ data, userNotes, setUserNotes, onBack, onSave, busy, canWrite }) {
  const guide = data.guide || {};
  const dims = guide.dimensions || [];
  const [openKey, setOpenKey] = useState(dims[0]?.key || null);

  return (
    <div className="space-y-5" data-testid="transition-guide-step">
      <button onClick={onBack} data-testid="guide-back-btn" className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] hover:text-[#1A3622] inline-flex items-center gap-1">
        <CaretLeft size={12} /> Back to classification
      </button>

      <div className="bg-white border border-[#E0DDD6] px-5 py-5">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
          Leo&apos;s summary · {data.scenario?.label}
        </div>
        <p className="text-sm text-[#0A192F] leading-relaxed" data-testid="guide-summary">{guide.summary || "—"}</p>
        {guide.next_step && (
          <div className="mt-4 pt-4 border-t border-[#E0DDD6]">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">First next step</div>
            <p className="text-sm text-[#1A3622] font-medium" data-testid="guide-next-step">{guide.next_step}</p>
          </div>
        )}
      </div>

      <div className="space-y-2" data-testid="guide-dimensions">
        {dims.map((d) => (
          <DimensionCard key={d.key} dim={d} open={openKey === d.key} onToggle={() => setOpenKey(openKey === d.key ? null : d.key)} />
        ))}
      </div>

      {data.market_practice?.disposition_rows?.length > 0 && (
        <details className="bg-white border border-[#E0DDD6] px-4 py-3" data-testid="disposition-channels">
          <summary className="text-sm text-[#0A192F] font-medium cursor-pointer">
            Disposition channels & market-practice reference
          </summary>
          <div className="mt-3 space-y-2 text-xs">
            {(data.market_practice.disposition_rows || []).map((r) => (
              <div key={r.id} className="border-t border-[#E0DDD6] pt-2">
                <div className="font-medium text-[#0A192F]">{r.name_or_channel}</div>
                <div className="text-[#5C5F58] mt-0.5">{r.controlled_value_or_timeline}</div>
                {r.cost_or_fee && <div className="text-[#5C5F58] italic mt-0.5">Fees: {r.cost_or_fee}</div>}
                <div className="text-[10px] text-[#8C6B0E] mt-1">{r.authority_tier}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {r.source_records?.map((s) => <SourcePill key={s.id} src={s} />)}
                </div>
                <div className="text-[10px] text-[#6B1718] mt-1 italic">Guardrail: {r.gpt_constraint}</div>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="bg-white border border-[#E0DDD6] px-4 py-4">
        <label className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
          Your notes (optional)
        </label>
        <textarea
          value={userNotes}
          onChange={(e) => setUserNotes(e.target.value)}
          rows={2}
          placeholder="e.g. Focus on the study first; family walkthrough on the 15th."
          data-testid="guide-user-notes"
          className="w-full border border-[#E0DDD6] bg-white px-3 py-2 text-sm focus:border-[#1A3622] focus:outline-none resize-none"
        />
      </div>

      <button
        onClick={onSave}
        disabled={busy || !canWrite}
        data-testid="save-plan-btn"
        className="w-full bg-[#1A3622] text-[#F9F8F6] py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        {busy ? <><CircleNotch size={14} className="animate-spin" /> Saving…</> : <><FloppyDisk size={14} weight="bold" /> Save this plan to your ledger</>}
      </button>
    </div>
  );
}

function DimensionCard({ dim, open, onToggle }) {
  return (
    <div className="bg-white border border-[#E0DDD6]" data-testid={`dim-${dim.key}`}>
      <button
        onClick={onToggle}
        data-testid={`dim-toggle-${dim.key}`}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#F9F8F6] text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 bg-[#C5A059]" />
          <div>
            <div className="text-sm text-[#0A192F] font-medium">{dim.label}</div>
            {dim.narrative && <div className="text-xs text-[#5C5F58] mt-0.5 line-clamp-1">{dim.narrative.slice(0, 90)}…</div>}
          </div>
        </div>
        <CaretRight size={14} className={`text-[#5C5F58] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[#E0DDD6] px-4 py-4 space-y-3">
          <p className="text-sm text-[#0A192F] leading-relaxed" data-testid={`dim-narrative-${dim.key}`}>{dim.narrative}</p>

          {(dim.actions || []).length > 0 && (
            <div>
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">Actions this week</div>
              <ul className="space-y-1.5">
                {dim.actions.map((a, i) => (
                  <li key={i} className="text-xs text-[#0A192F] flex gap-2">
                    <CheckCircle size={12} className="text-[#1A3622] mt-0.5 shrink-0" />
                    <div><strong>{a.title}.</strong> <span className="text-[#5C5F58]">{a.detail}</span></div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(dim.caveats || []).length > 0 && (
            <div className="bg-[#F4E9D2] border-l-2 border-[#C5A059] px-3 py-2">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C3D08] mb-1">Guardrails</div>
              <ul className="space-y-1">
                {dim.caveats.map((c, i) => (
                  <li key={i} className="text-xs text-[#5C3D08]">{c}</li>
                ))}
              </ul>
            </div>
          )}

          {(dim.citation_records || []).length > 0 && (
            <div>
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">Sources</div>
              <div className="flex flex-wrap gap-1">
                {dim.citation_records.map((s) => <SourcePill key={s.id} src={s} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SourcePill({ src }) {
  const inner = (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] border border-[#E0DDD6] bg-white text-[#0A192F] hover:border-[#1A3622]" data-testid={`source-pill-${src.id}`}>
      <strong>{src.id}</strong>
      <span className="text-[#5C5F58]">· {src.label?.slice(0, 40) || src.domain}</span>
      {src.official_url && <ArrowSquareOut size={8} className="text-[#5C5F58]" />}
    </span>
  );
  if (src.official_url) {
    return <a href={src.official_url} target="_blank" rel="noreferrer" title={src.label}>{inner}</a>;
  }
  return <span title={src.label}>{inner}</span>;
}
